/** Matches the estate's historical member spreadsheet layout. */

export type MemberImportFieldKey =
  | 'plotNumber'
  | 'name'
  | 'phone'
  | 'email'
  | 'boreholeMeterNumber'
  | 'kplcMeterNumber'

export interface MemberImportSheetColumn {
  header: string
  importKey?: MemberImportFieldKey
  /** Show * in the format guide for required columns */
  required?: boolean
}

export const MEMBER_IMPORT_SHEET_COLUMNS: MemberImportSheetColumn[] = [
  { header: 'Plot No.', importKey: 'plotNumber' },
  { header: 'Name', importKey: 'name', required: true },
  { header: 'Telephone number', importKey: 'phone', required: true },
  { header: 'Email', importKey: 'email' },
  { header: 'Borehole meter no.', importKey: 'boreholeMeterNumber' },
  { header: 'KPLC meter no.', importKey: 'kplcMeterNumber' },
]

/** Example row for the downloadable template. */
export const MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW: string[] = [
  '322',
  'Peter Kamau',
  '254722576609',
  'member@example.com',
  '00895',
  '12345678',
]

export function normalizeImportHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, '_')
}

const HEADER_ALIASES: Record<MemberImportFieldKey, string[]> = {
  plotNumber: ['plot_no', 'plot_number', 'plot', 'plotnumber'],
  name: ['name', 'full_name', 'member_name'],
  phone: ['telephone_number', 'telephone', 'phone', 'phone_number', 'mobile'],
  email: ['email', 'email_address'],
  boreholeMeterNumber: ['borehole_meter_no', 'borehole_meter_number', 'borehole_meter', 'borehole'],
  kplcMeterNumber: ['kplc_meter_no', 'kplc_meter_number', 'kplc_meter', 'kplc'],
}

/** Map normalized header keys to import field keys. */
export function mapImportHeaderFields(
  headers: string[],
): Partial<Record<MemberImportFieldKey, number>> {
  const map: Partial<Record<MemberImportFieldKey, number>> = {}

  headers.forEach((header, index) => {
    const key = normalizeImportHeader(header)
    for (const [field, aliases] of Object.entries(HEADER_ALIASES) as [
      MemberImportFieldKey,
      string[],
    ][]) {
      if (aliases.includes(key)) {
        map[field] = index
      }
    }
  })

  return map
}

/** Normalize Kenyan phone numbers to +254XXXXXXXXX for validation and storage. */
export function normalizeKenyaPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''

  if (digits.startsWith('254') && digits.length === 12) {
    return `+${digits}`
  }
  if (digits.startsWith('0') && digits.length === 10) {
    return `+254${digits.slice(1)}`
  }
  if (digits.length === 9 && /^[17]/.test(digits)) {
    return `+254${digits}`
  }
  if (raw.trim().startsWith('+')) {
    return `+${digits}`
  }

  return raw.trim()
}

/** Plot row used when resolving import plot numbers against the registry. */
export interface MemberImportPlotRef {
  id: string
  newNumber: string
  oldNumber: string
  boreholeMeterNumber: string | null
  kplcMeterNumber: string | null
}

/**
 * Normalize plot numbers from spreadsheet cells (e.g. Excel `322.0`, leading zeros).
 * Residential plots resolve to canonical numeric strings; special-use keeps SPECIAL-* form.
 */
export function normalizeImportPlotNumber(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''

  if (/^special/i.test(trimmed)) {
    return trimmed.replace(/\s+/g, '-').toUpperCase()
  }

  const withoutDecimals = trimmed.replace(/,/g, '')
  if (/^\d+(\.0+)?$/.test(withoutDecimals)) {
    return String(Number.parseInt(withoutDecimals, 10))
  }

  const digits = trimmed.replace(/\D/g, '')
  if (digits && digits === trimmed.replace(/\./g, '')) {
    return String(Number.parseInt(digits, 10))
  }

  return trimmed
}

/** Index plots by new/old numbers and normalized keys for import lookup. */
export function buildMemberImportPlotIndex(
  plots: MemberImportPlotRef[],
): Map<string, MemberImportPlotRef> {
  const index = new Map<string, MemberImportPlotRef>()

  const addKey = (key: string, plot: MemberImportPlotRef) => {
    const k = key.trim()
    if (!k) return
    if (!index.has(k)) index.set(k, plot)
  }

  for (const plot of plots) {
    addKey(plot.newNumber, plot)
    addKey(plot.oldNumber, plot)
    addKey(normalizeImportPlotNumber(plot.newNumber), plot)
    addKey(normalizeImportPlotNumber(plot.oldNumber), plot)
  }

  return index
}

/** Resolve a spreadsheet plot number to a plot in the registry, or null if not found. */
export function resolveMemberImportPlot(
  plotNumber: string | null | undefined,
  plotIndex: Map<string, MemberImportPlotRef>,
): MemberImportPlotRef | null {
  if (!plotNumber?.trim()) return null

  const trimmed = plotNumber.trim()
  const normalized = normalizeImportPlotNumber(trimmed)

  return plotIndex.get(trimmed) ?? (normalized ? plotIndex.get(normalized) : undefined) ?? null
}
